import type { KeyDetection } from "@midi-fantom-utils/midi-util";
import { pluralizeRu } from "../i18n.js";

export type HeaderInfoKind = "source" | "preview";

type HeaderInfoProps = {
  title: string;
  kind: HeaderInfoKind;
  fileName: string;
  format: 0 | 1 | 2;
  trackCount: number;
  durationLabel: string;
  durationTicks: number;
  tempos: ReadonlyArray<{ bpm: number; ticks: number }>;
  timeSignatures: ReadonlyArray<{ value: string; ticks: number }>;
  keySignatures: ReadonlyArray<{ key: string; scale: "major" | "minor"; ticks: number }>;
  detectedKey: KeyDetection;
  detectedKeySource?: KeyDetection;
  highlight?: boolean;
};

const formatConfidence = (c: number): string => c.toFixed(2);

const formatDetectedKeyLabel = (d: KeyDetection): string => {
  if (d.best === null) {
    if (d.noteCount === 0) return "недостаточно нот";
    return "не удалось определить";
  }
  return `${d.best.key} ${d.best.scale} (уверенность ${formatConfidence(d.best.confidence)})`;
};

export const HeaderInfo = ({
  title,
  kind,
  fileName,
  format,
  trackCount,
  durationLabel,
  durationTicks,
  tempos,
  timeSignatures,
  keySignatures,
  detectedKey,
  detectedKeySource,
  highlight,
}: HeaderInfoProps) => {
  const d = detectedKey;
  const src = detectedKeySource ?? detectedKey;
  const showSourceBanner = kind === "source" && format === 0;
  return (
    <section className={`card${highlight ? " card--highlight" : ""}`}>
      <h2 className="card__title">{title}</h2>
      <dl className="kv">
        <dt>Файл</dt>
        <dd>{fileName || "—"}</dd>
        <dt>Формат</dt>
        <dd>{format} · {trackCount} {pluralTracks(trackCount)}</dd>
        <dt>Длительность</dt>
        <dd>
          {durationLabel} ({durationTicks} тиков)
        </dd>
        <dt>Темп</dt>
        <dd>
          {tempos.length === 0
            ? "—"
            : tempos.map((t) => `${Math.ceil(t.bpm)} BPM @ ${t.ticks}`).join(", ")}
        </dd>
        <dt>Размерность</dt>
        <dd>
          {timeSignatures.length === 0
            ? "—"
            : timeSignatures.map((t) => `${t.value} @ ${t.ticks}`).join(", ")}
        </dd>
        <dt>Тональность (заявлено)</dt>
        <dd>
          {keySignatures.length === 0
            ? "—"
            : keySignatures
                .map((k) => `${k.key} ${k.scale} @ ${k.ticks}`)
                .join(", ")}
        </dd>
        <dt>Тональность (детектор)</dt>
        <dd>
          {formatDetectedKeyLabel(d)}
          {d.noteCount > 0 && (
            <span className="kv__hint"> · {d.noteCount} нот</span>
          )}
        </dd>
      </dl>
      {d.best !== null && d.alternatives.filter(
        (a) => a !== null && (a.key !== d.best!.key || a.scale !== d.best!.scale)
      ).length > 0 && (
        <ul className="kv__alts">
          {d.alternatives
            .filter(
              (a) => a !== null && (a.key !== d.best!.key || a.scale !== d.best!.scale)
            )
            .map((a) => (
              <li key={`${a!.key}-${a!.scale}`}>
                альтернатива: {a!.key} {a!.scale} ({formatConfidence(a!.confidence)})
              </li>
            ))}
        </ul>
      )}
      {showSourceBanner ? (
        <p className="kv__diff">
          ⚠ Файл в формате 0 — все каналы смешаны в одном MTrk.
          Раскладка по каналам ниже в трэках источника.
        </p>
      ) : null}
      {kind === "preview" && src.best !== null && detectedKey.best !== null && (
        src.best.key !== detectedKey.best.key || src.best.scale !== detectedKey.best.scale
      ) ? (
        <p className="kv__diff">
          Детектор для источника: {src.best.key} {src.best.scale};
          после трансформации: {detectedKey.best.key} {detectedKey.best.scale}.
        </p>
      ) : null}
    </section>
  );
};

const pluralTracks = (n: number): string =>
  pluralizeRu(n, ["трек", "трека", "треков"]);